import { useCallback, useState } from "react"
import { gql } from "~/__generated__"
import { EVENTS, trackEvent } from "~/common/analytics"
import { AttributionData, getLastTouchAttribution } from "~/common/attribution"
import { isMobile } from "~/common/platform"
import { useRecaptcha } from "~/hooks/use-recaptcha"
import { useToast } from "~/ui/use-toast"
import { downloadPath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { getInviterData } from "~/share/share-embedded-data"
import { getMetaContentMaybe } from "~/common/get-meta-content"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const SEND_DOWNLOAD_APP_EMAIL = gql(/* GraphQL */ `
  mutation SendDownloadAppEmail($input: SendDownloadAppEmailInput!) {
    sendDownloadAppEmail(input: $input) {
      success
      errors
    }
  }
`)

const SEND_FRIEND_INVITE_EMAIL = gql(/* GraphQL */ `
  mutation SendFriendInviteEmail($input: SendFriendInviteEmailInput!) {
    sendFriendInviteEmail(input: $input) {
      success
      errors
    }
  }
`)

const SEND_ROOM_SHARE_EMAIL = gql(/* GraphQL */ `
  mutation SendRoomShareEmail($input: SendRoomShareEmailInput!) {
    sendRoomShareEmail(input: $input) {
      success
      errors
    }
  }
`)

type EmailMutationHandler = (params: {
  email: string
  attribution: AttributionData | null
}) => Promise<void>

export type EmailSubmissionParams = {
  trackingData?: Record<string, unknown>
  successTitle?: string
  successMessage?: string
}

const useBaseEmailSubmission = () => {
  const [emailValue, setEmailValue] = useState("")
  const { toast } = useToast()
  // V2 flag: whether the share-page email path actually delivers an email. When
  // off, the email only correlates enrollments — UI must not promise a link.
  const sendShareEmail = getMetaContentMaybe("send-email") === "true"

  const submitEmail = useCallback(
    async ({
      trackingData,
      successMessage,
      successTitle = "Success",
      handleSubmit,
    }: EmailSubmissionParams & { handleSubmit: EmailMutationHandler }) => {
      const email = emailValue.trim()

      if (!EMAIL_REGEX.test(email)) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address",
          variant: "destructive",
        })
        return false
      }

      const attribution = getLastTouchAttribution()

      const trackItemPressed = (success: boolean, label?: string) => {
        trackEvent(EVENTS.ITEM_PRESSED, {
          ...trackingData,
          email,
          attribution_data: attribution,
          success,
          label,
        })
      }

      const prevEmailValue = emailValue
      setEmailValue("")

      try {
        await handleSubmit({ email, attribution })
        const defaultSuccessMessage = isMobile()
          ? "Download the app to join."
          : "Check your email for a link to download the app."

        // In V1 no email is sent, so don't promise a link — send them to download.
        const description = sendShareEmail
          ? successMessage || defaultSuccessMessage
          : "Download the app to join the chat."

        toast({
          title: successTitle,
          description,
          duration: 15000,
        })
        trackItemPressed(true)

        setTimeout(() => {
          window.location.href = downloadPath({})
        }, 3000)

        return true
      } catch (error) {
        setEmailValue(prevEmailValue)
        const errorMessage = error instanceof Error ? error.message : "An error occurred"
        console.error("Email submission error:", error)
        trackItemPressed(false, errorMessage)

        setTimeout(() => {
          window.location.href = downloadPath({})
        }, 3000)

        return false
      }
    },
    [emailValue, toast, sendShareEmail]
  )

  return { emailValue, setEmailValue, submitEmail }
}

const handleMutationFailure = (message?: string) => {
  const errorMessage = message ?? "Unable to send email. Please try again."
  throw new Error(errorMessage)
}

export const useDownloadAppEmailSubmission = () => {
  const [sendAppDownloadEmail, { loading }] = useSafeMutation(SEND_DOWNLOAD_APP_EMAIL)
  const { verifyRecaptcha } = useRecaptcha()
  const handleSubmit: EmailMutationHandler = useCallback(
    async ({ email, attribution }) => {
      const result = await sendAppDownloadEmail({
        variables: {
          input: {
            email,
            attributionData: attribution,
            recaptchaToken: await verifyRecaptcha("send_email"),
          },
        },
      })

      if (!result.data?.sendDownloadAppEmail?.success) {
        handleMutationFailure(result.data?.sendDownloadAppEmail?.errors?.[0])
      }
    },
    [sendAppDownloadEmail, verifyRecaptcha]
  )
  const { submitEmail: internalSubmitEmail, ...handles } = useBaseEmailSubmission()

  const submitEmail = useCallback(
    (params: EmailSubmissionParams) => {
      return internalSubmitEmail({ ...params, handleSubmit })
    },
    [internalSubmitEmail, handleSubmit]
  )

  return { ...handles, mutationPending: loading, submitEmail }
}

export const useInviteFriendEmailSubmission = () => {
  const [sendInviteFriendEmail, { loading }] = useSafeMutation(SEND_FRIEND_INVITE_EMAIL)
  const { verifyRecaptcha } = useRecaptcha()
  const handleSubmit: EmailMutationHandler = useCallback(
    async ({ email, attribution }) => {
      const result = await sendInviteFriendEmail({
        variables: {
          input: {
            email,
            attributionData: attribution,
            recaptchaToken: await verifyRecaptcha("send_email"),
            inviterId: getInviterData()?.id,
          },
        },
      })

      if (!result.data?.sendFriendInviteEmail?.success) {
        handleMutationFailure(result.data?.sendFriendInviteEmail?.errors?.[0])
      }
    },
    [sendInviteFriendEmail, verifyRecaptcha]
  )
  const { submitEmail: internalSubmitEmail, ...handles } = useBaseEmailSubmission()

  const submitEmail = useCallback(
    (params: EmailSubmissionParams) => {
      return internalSubmitEmail({ ...params, handleSubmit })
    },
    [internalSubmitEmail, handleSubmit]
  )

  return { ...handles, mutationPending: loading, submitEmail }
}

export const useRoomShareEmailSubmission = () => {
  const [sendRoomShareEmail, { loading }] = useSafeMutation(SEND_ROOM_SHARE_EMAIL)
  const { verifyRecaptcha } = useRecaptcha()

  const { submitEmail: internalSubmitEmail, ...handles } = useBaseEmailSubmission()

  const submitEmail = useCallback(
    ({
      roomId,
      additionalRoomIds,
      ...params
    }: EmailSubmissionParams & { roomId: string; additionalRoomIds?: string[] }) => {
      const handleSubmit: EmailMutationHandler = async ({ email, attribution }) => {
        const result = await sendRoomShareEmail({
          variables: {
            input: {
              email,
              roomId,
              additionalRoomIds,
              inviterId: getInviterData()?.id,
              attributionData: attribution,
              recaptchaToken: await verifyRecaptcha("send_email"),
            },
          },
        })

        if (!result.data?.sendRoomShareEmail?.success) {
          handleMutationFailure(result.data?.sendRoomShareEmail?.errors?.[0])
        }
      }

      return internalSubmitEmail({ ...params, handleSubmit })
    },
    [internalSubmitEmail, sendRoomShareEmail, verifyRecaptcha]
  )

  return { ...handles, mutationPending: loading, submitEmail }
}
